import { useEffect, useMemo, useState } from "react";
import CategoryPill from "@/components/base/CategoryPill";
import Reveal from "@/components/base/Reveal";
import { memes } from "@/mocks/memes";
import { categories } from "@/mocks/categories";
import { formatHeat } from "@/utils/format";

type Meme = (typeof memes)[number];

export default function MemeGallery() {
  const [active, setActive] = useState("all");
  const [selected, setSelected] = useState<Meme | null>(null);

  const filtered = useMemo(
    () => (active === "all" ? memes : memes.filter((meme) => meme.category === active)),
    [active]
  );

  useEffect(() => {
    if (!selected) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelected(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected]);

  return (
    <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
      <div className="flex flex-wrap gap-2">
        <CategoryPill label="全部图集" icon="ri-apps-2-line" active={active === "all"} onClick={() => setActive("all")} />
        {categories.map((category) => (
          <CategoryPill
            key={category.key}
            label={category.name}
            icon={category.icon}
            active={active === category.key}
            onClick={() => setActive(category.key)}
          />
        ))}
      </div>

      <p className="mt-5 text-sm text-foreground-500" role="status">
        当前分区共 <strong className="font-medium text-foreground-900">{filtered.length}</strong> 张素材，点击图片可放大查看使用场景
      </p>

      {filtered.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((meme, index) => (
            <Reveal key={meme.id} delay={index * 50}>
              <button
                type="button"
                onClick={() => setSelected(meme)}
                className="group block w-full cursor-pointer overflow-hidden rounded-lg border border-background-200 bg-background-50 text-left transition-colors hover:border-primary-300"
                aria-label={`查看表情包：${meme.title}`}
              >
                <span className="block aspect-square w-full overflow-hidden bg-background-100">
                  <img
                    src={meme.image}
                    alt={`${meme.title} 表情包素材`}
                    title={`${meme.title} - 513吃瓜表情包`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                </span>
                <span className="block p-4">
                  <span className="block text-sm font-medium text-foreground-950">{meme.title}</span>
                  <span className="mt-2 flex items-center justify-between text-xs text-foreground-500">
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-800">
                      {meme.categoryName}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-3-line text-primary-500" aria-hidden="true"></i>
                      {formatHeat(meme.likes)}
                    </span>
                  </span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      ) : (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-500">
            <i className="ri-image-2-line" aria-hidden="true"></i>
          </span>
          <p className="mt-4 text-base text-foreground-700">这个分区暂时还没有素材</p>
          <button
            type="button"
            onClick={() => setActive("all")}
            className="mt-4 cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 hover:bg-primary-600"
          >
            看看全部图集
          </button>
        </div>
      )}

      {selected ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`表情包详情：${selected.title}`}
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-2xl overflow-hidden rounded-lg border border-background-200 bg-background-50"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-3.5">
              <h2 className="font-heading text-lg text-foreground-950">{selected.title}</h2>
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[280px_minmax(0,1fr)]">
              <div className="aspect-square w-full bg-background-100">
                <img
                  src={selected.image}
                  alt={`${selected.title} 表情包大图`}
                  title={`${selected.title} - 513吃瓜表情包`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <span className="inline-flex rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                  {selected.categoryName}
                </span>
                <h3 className="mt-4 font-heading text-base text-foreground-950">使用场景</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{selected.usage}</p>
                <p className="mt-5 flex items-center gap-1.5 text-sm text-foreground-500">
                  <i className="ri-heart-3-line text-primary-500" aria-hidden="true"></i>
                  {formatHeat(selected.likes)} 人收藏过这张
                </p>
                <a
                  href={selected.image}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-download-2-line" aria-hidden="true"></i>
                  打开原图保存
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}